import React from 'react';
import { 
  UserPlus, 
  Target, 
  Zap, 
  TrendingUp,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const steps = [
  {
    icon: UserPlus,
    number: "01",
    title: "Tell us about yourself",
    description: "Share your profession, experience level, and career goals so we can create your personalized plan.",
    features: ["Quick 5-minute setup", "Role-specific questions", "Experience assessment"]
  },
  {
    icon: Target,
    number: "02", 
    title: "Get your AI-powered plan",
    description: "Our AI analyzes your profile and creates a custom career advancement plan with specific milestones.",
    features: ["Personalized roadmap", "Industry insights", "Skill gap analysis"]
  },
  {
    icon: Zap,
    number: "03",
    title: "Complete exercises & track progress",
    description: "Work through tailored exercises, challenges, and learning modules designed for your profession.",
    features: ["Interactive exercises", "Progress tracking", "Achievement badges"]
  },
  {
    icon: TrendingUp,
    number: "04",
    title: "Advance your career",
    description: "Use salary insights, resume tools, and negotiation strategies to level up in your profession.",
    features: ["Salary benchmarks", "Resume optimization", "Interview prep"]
  }
];

export default function HowItWorks() {
  return (
    <section id="how-it-works" className="py-20 bg-background">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            How it works
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Get started in minutes and see results in weeks. Our proven process helps thousands advance their careers.
          </p>
        </div>

        <div className="space-y-12 lg:space-y-16">
          {steps?.map((step, index) => {
            const IconComponent = step?.icon;
            const isEven = index % 2 === 0;
            
            return (
              <div key={index} className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 lg:gap-16`}>
                {/* Content */}
                <div className="flex-1 text-center lg:text-left">
                  <div className="flex items-center justify-center lg:justify-start gap-4 mb-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-foreground font-bold text-lg">
                      {step?.number}
                    </div>
                    <div className="p-3 rounded-xl bg-primary/10">
                      {IconComponent && <IconComponent className="w-6 h-6 text-primary" />}
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-semibold text-foreground mb-4">
                    {step?.title}
                  </h3>
                  <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                    {step?.description}
                  </p>
                  
                  <div className="space-y-3">
                    {step?.features?.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center justify-center lg:justify-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Visual */}
                <div className="flex-1 relative">
                  <div className={`relative w-full max-w-md mx-auto h-64 rounded-2xl bg-gradient-to-br ${
                    index === 0 ? 'from-blue-500 to-cyan-500' :
                    index === 1 ? 'from-purple-500 to-pink-500' :
                    index === 2 ? 'from-orange-500 to-yellow-500': 'from-green-500 to-emerald-500'
                  } p-8 flex items-center justify-center shadow-xl`}>
                    {IconComponent && <IconComponent className="w-20 h-20 text-white/80" />}
                    
                    {/* Step connector line */}
                    {index < steps?.length - 1 && (
                      <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 hidden lg:block">
                        <div className="w-px h-12 bg-border"></div>
                        <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                          <ArrowRight className="w-4 h-4 text-muted-foreground rotate-90" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-4 bg-primary/5 px-6 py-1 rounded-xl border border-primary/20">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-foreground font-medium">
              Average users see career progress within 30 days
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}